import React, { useEffect, useRef, useState, useCallback } from 'react';
import playIcon from '../../assets/icons/audio-play.svg';
import pauseIcon from '../../assets/icons/audio-pause.svg';
import { Button } from 'antd';
import { AudioAnalyzer } from '../../utils/audioAnalysis';
import './index.less';

type AudioPlayerProps = {
  audioUrl: string;
  autoPlay?: boolean;
};

const AudioPlayerComponent: React.FC<AudioPlayerProps> = ({ audioUrl, autoPlay = false }) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(autoPlay);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [analyzedUrl, setAnalyzedUrl] = useState<string>('');
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (audioRef.current) {
      if (autoPlay) {
        audioRef.current.play().catch(() => {
          setIsPlaying(false);
        });
      }
    }
  }, [audioUrl, autoPlay]);

  // Analyze audio file using shared utility
  const analyzeAudioFile = useCallback(async (url: string) => {
    if (isAnalyzing) return;
    
    setIsAnalyzing(true);
    const result = await AudioAnalyzer.analyzeAudio(url, 50, 12);
    setWaveformData(result.waveformData);
    setIsAnalyzing(false);
  }, [isAnalyzing]);

  // Analyze audio when URL changes
  useEffect(() => {
    if (audioUrl && audioUrl !== analyzedUrl && !isAnalyzing) {
      setWaveformData(AudioAnalyzer.generateFallbackWaveform(50));
      
      const timeoutId = setTimeout(() => {
        analyzeAudioFile(audioUrl);
        setAnalyzedUrl(audioUrl);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [audioUrl, analyzedUrl, isAnalyzing, analyzeAudioFile]);

  // Cleanup on unmount
  useEffect(() => {
    return () => AudioAnalyzer.cleanup();
  }, []);


  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsLoaded(true);
    }
  };

  const handleSeek = (value: number) => {
    if (audioRef.current && duration > 0) {
      const newTime = (value / 100) * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleWaveformClick = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const waveformContainer = e.currentTarget;
    const rect = waveformContainer.getBoundingClientRect();

    let clientX: number;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
    } else {
      clientX = e.clientX;
    }

    const offsetX = clientX - rect.left;
    const containerWidth = rect.width;
    const seekPercentage = (offsetX / containerWidth) * 100;

    handleSeek(seekPercentage);
  };

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);
  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };


  return (
    <div className="audio-player-wrapper">
      <div className="audio-inline-row">
        <Button
          type="text"
          onClick={togglePlay}
          icon={isPlaying ? <img src={pauseIcon} alt="pause" className="play-pause-icon" /> : <img src={playIcon} alt="play" className="play-pause-icon" />}
          className="control-button"
        />

        <div
          className="waveform-container"
          onClick={handleWaveformClick}
          onTouchStart={handleWaveformClick}
          role="button"
          tabIndex={0}
          aria-label="Audio waveform"
        >
          {(waveformData.length > 0 ? waveformData : AudioAnalyzer.generateFallbackWaveform(50)).map((height, index, array) => {
            const progress = duration > 0 ? currentTime / duration : 0;
            const isPlayed = index < progress * array.length;

            return (
              <div
                key={index}
                className={`waveform-bar ${isPlayed ? 'played' : ''} ${isAnalyzing ? 'analyzing' : ''}`}
                style={{
                  height: `${Math.max(height * 100, 8)}%`,
                  opacity: isAnalyzing ? 0.6 : 1
                }}
              />
            );
          })}
        </div>

        <div className="time-inline">{formatTime(duration)}</div>
      </div>

      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default AudioPlayerComponent;
